import React, { useState, useEffect } from 'react';
import PropertyWizard from './PropertyWizard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { showToast } from '../../../../components/common/notification/Notification';
import { useNavigate } from "react-router-dom";
import { getUserTypeName } from '../../../navigation/adminNavigation';

const PropertyForm = () => {
  const navigate = useNavigate();

  const loggedInUser = useSelector((state) => state.auth.user);
  const authState = useSelector((state) => state.auth);
  const token = loggedInUser?.refreshToken || loggedInUser?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [propertyForm, setPropertyForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Default status is the "New" entry from /property/statusList, same as the mobile app
  const [defaultStatusId, setDefaultStatusId] = useState(null);

  useEffect(() => {
    const fetchDefaultStatus = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/property/statusList`,
          { page_number: 1, page_size: 20, order_by: 'ASC' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === 'SUCCESS') {
          const newStatus = (response.data.propertyStatuss || []).find((s) => s.name === 'New');
          if (newStatus) setDefaultStatusId(newStatus.id);
        }
      } catch (error) {
        console.error('Error fetching property status:', error);
      }
    };
    fetchDefaultStatus();
  }, [baseUrl, token]);

  // Submit handler of the review step
  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (defaultStatusId === null) {
      showToast('Unable to load property status. Please refresh and try again.', { type: 'error' });
      return;
    }

    setIsSubmitting(true);
    // Mobile sends uploaded images and videos together in `images`; the backend moves each
    // file using its `imagePath` (the uploaded file name), so it must be included.
    const images = [...(propertyForm.images || []), ...(propertyForm.videos || [])].map((img) => ({
      imagePath: img.name,
      image_url: img.url,
    }));

    const videos = [];

    const attachments = (propertyForm.attachments || []).map((a) => ({
      attachmentPath: a.name,
    }));

    // `featured_property` (the old "New" Yes/No input) is not sent: the backend field is a
    // primitive boolean that defaults to false when it is missing.
    const payload = {
      city_name: propertyForm.cityName || '',
      state_name: propertyForm.stateName || '',
      country_name: "India",
      area_name: propertyForm.area || '',
      property_type_id: propertyForm.propertyType || null,
      purpose_id: propertyForm.propertyPurpose || null,
      property_status_id: defaultStatusId,
      sub_type_id: propertyForm.propertySubtype || null,
      structure: propertyForm.structure || {},
      feature: propertyForm.feature || {},
      images,
      videos,
      attachments,
      tags: propertyForm.tags || '',
      name: propertyForm.name || '',
      description: propertyForm.description || '',
      address: propertyForm.address || '',
      land_mark: propertyForm.tags || '',
      pin_code: propertyForm.pin_code || '',
      price: propertyForm.price || '',
      land_type_id: propertyForm.landType || null,
      loan_availability: propertyForm.loanAvailability,
      are: propertyForm.are || '',
      sub_sub_type_id: propertyForm.propertySubSubType || 0,
      lead_id: 0,
      active: true,
    };

    try {
      const response = await axios.post(`${baseUrl}/property/addProperty`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.statusType === 'SUCCESS') {
        showToast('Property created successfully!' , { type: 'success' });
        const userType = getUserTypeName(authState);
        if (userType === 'CUSTOMER') {
          // Same as mobile: customers earn a reward for adding a property
          try {
            await axios.post(`${baseUrl}/customer/rewards/user/ADD_PROPERTY`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (rewardsError) {
            showToast('Property added, but rewards update failed', { type: 'warning' });
          }
        }
        // isSubmitting stays true until the redirect, which also lifts the unsaved-changes warning
        setTimeout(() => {
          if (userType === 'CUSTOMER') {
            navigate("/customer/my-properties");
          } else if (userType === 'PARTNER') {
            navigate("/partner/my-properties");
          } else {
            navigate("/property");
          }
        }, 1500);
      } else {
        // The form state is kept so nothing the user entered is lost
        setIsSubmitting(false);
        showToast(response.data.text || 'Failed to submit property', { type: 'error' });
      }
    } catch (error) {
      setIsSubmitting(false);
      showToast('Failed to submit property data', { type: 'error' });
      console.error("Error submitting property form:", error);
    }
  };

  return (
    <PropertyWizard
      mode="create"
      form={propertyForm}
      setForm={setPropertyForm}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isDirty={Object.keys(propertyForm).length > 0}
    />
  );
};

export default PropertyForm;
